/**
 * 위험요인 해소 모달
 * 발견사항 선택 + 다중 사진 업로드 + 조치사항 입력
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useToastStore } from '../../../stores/toast';
import { extractApiError } from '../../../lib/api';
import { EditModalFooter } from '../../common/EditModalFooter';
import {
  resolveWithEvidence,
  uploadResolutionPhoto,
} from '../../../lib/inspections';
import Modal from '../../Modal';
import { ActionItemsInput, notesToItems, itemsToNotes } from './ActionItemsInput';
import { SeverityBadge } from './SeverityBadge';

interface PhotoPreview {
  file?: File;
  previewUrl: string;
  /** 이미 서버에 업로드된 사진 정보 (수정 모드) */
  uploaded?: { url: string; thumbnailUrl?: string; fileId?: string };
}

/** 모달에서 필요한 최소 발견사항 정보 */
interface FindingInfo {
  id: string;
  category: string;
  subCategory?: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';
  resolutionStatus?: 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL';
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  resolutionPhotoUrl?: string;
  resolvedAt?: string;
  notes?: string;
  deletedAt?: string;
  measures?: string[];
  aiLikelihoodScore?: number | null;
  aiSeverityScore?: number | null;
}

interface HazardResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  inspectionId: string;
  companyId: string;
  /** 해소 대상 발견사항 목록 (현재 사진의 발견사항) */
  findings: FindingInfo[];
  /** 사전 선택된 발견사항 ID (위험요인별 버튼에서 진입 시) */
  preSelectedFindingId?: string;
  /** 버퍼 모드: 사진 업로드만 수행하고 API 호출 없이 콜백으로 데이터 반환 */
  onBufferedSave?: (findingId: string, data: {
    notes: string;
    photos: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  }) => void;
  /** Phase N3: 위험성평가 설정 (score 기반 SeverityBadge 렌더링용) */
  riskConfig?: RiskAssessmentConfig | null;
}

const MAX_PHOTOS = 5;

/**
 * 푸터의 제출 버튼이 본문의 폼을 가리키는 `form` 속성 값. base Modal이 본문과 푸터를 형제
 * div로 렌더하므로 이 연결 없이는 `submitType="submit"`이 아무 폼에도 속하지 않는다.
 */
const FORM_ID = 'hazard-resolution-form';

interface ResolutionFormValues {
  actionItems: string[];
  photos: PhotoPreview[];
  representativeIndex: number;
}

/**
 * 선택된 발견사항의 폼 초기값 — **폼 프리필과 dirty 기준점이 같은 계산을 쓰도록** 순수 함수로
 * 뺐다(결정 기록 `2026-08-16-edit-modal-dirty-gate-standard.md`).
 *
 * 표준 부품 `useDirtySnapshot`의 capture 방식을 쓰지 않은 이유: 여기 프리필은 setState라
 * 같은 커밋에서 잡은 기준점에는 프리필 값이 아직 없다(열자마자 전체가 변경으로 잡힌다).
 * 기준점이 선택된 발견사항만으로 결정되므로 파생값으로 두는 편이 타이밍에 안전하다.
 */
function buildResolutionPrefill(finding: FindingInfo): ResolutionFormValues {
  if (finding.resolutionStatus !== 'COMPLIED') {
    // 새 해소: 사진은 비우고, 권고 대책이 있으면 조치사항으로 프리필.
    return {
      actionItems: finding.measures?.length ? [...finding.measures] : [],
      photos: [],
      representativeIndex: 0,
    };
  }

  const photos: PhotoPreview[] = [];
  if (finding.resolutionPhotos && finding.resolutionPhotos.length > 0) {
    finding.resolutionPhotos.forEach((p) => {
      photos.push({ previewUrl: p.thumbnailUrl || p.url, uploaded: p });
    });
  } else if (finding.resolutionPhotoUrl) {
    photos.push({
      previewUrl: finding.resolutionPhotoUrl,
      uploaded: { url: finding.resolutionPhotoUrl },
    });
  }

  // 저장된 대표 사진 URL과 매칭하여 인덱스 복원
  let representativeIndex = 0;
  if (finding.resolutionPhotoUrl && photos.length > 1) {
    const matchIdx = photos.findIndex((p) => p.uploaded?.url === finding.resolutionPhotoUrl);
    representativeIndex = matchIdx >= 0 ? matchIdx : 0;
  }

  return {
    actionItems: notesToItems(finding.notes || ''),
    photos,
    representativeIndex,
  };
}

/** 저장 결과를 좌우하는 값만 추린 비교용 스냅샷 — 새로 고른 파일은 blob URL이라 고유하다. */
function resolutionSnapshot(values: ResolutionFormValues): string {
  return JSON.stringify({
    actionItems: values.actionItems,
    photos: values.photos.map((p) => p.uploaded?.url ?? p.previewUrl),
    representativeIndex: values.representativeIndex,
  });
}

/**
 * 푸터 — 취소가 base Modal의 requestClose로 수렴해야 미저장 닫기 확인을 탄다.
 * (context는 Modal children/footer의 **하위 컴포넌트**에서만 잡히므로 분리했다. onClose를
 * 직접 부르면 확인을 우회한다 — base Modal 규약)
 *
 * `submitForm`이 필수다 — base가 본문과 푸터를 **형제 div**로 렌더하므로 이 푸터는 본문의
 * `<form>` 밖이고, 그 연결이 없으면 `submitType="submit"`이 조용히 아무 일도 하지 않는다.
 */
function ResolutionFooter({
  isUpdateMode,
  isDirty,
  isLoading,
  hasActionItems,
  onFallbackClose,
}: {
  isUpdateMode: boolean;
  isDirty: boolean;
  isLoading: boolean;
  hasActionItems: boolean;
  onFallbackClose: () => void;
}) {
  return (
    <EditModalFooter
      isDirty={isDirty}
      showHint={isUpdateMode}
      submitLabel={isUpdateMode ? '수정 완료' : '해소 완료'}
      submitType="submit"
      submitForm={FORM_ID}
      submitVariant="success"
      submitDisabled={isLoading || !hasActionItems || (isUpdateMode && !isDirty)}
      busy={isLoading}
      busyLabel="처리 중..."
      onFallbackClose={onFallbackClose}
    />
  );
}

export function HazardResolutionModal({
  isOpen,
  onClose,
  inspectionId,
  companyId,
  findings,
  preSelectedFindingId,
  onBufferedSave,
  riskConfig,
}: HazardResolutionModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // PENDING 발견사항만 필터 (수정 모드일 때는 COMPLIED도 포함)
  const selectableFindings = preSelectedFindingId
    ? findings.filter(f => f.id === preSelectedFindingId)
    : findings.filter(f => !f.deletedAt);

  // 폼 상태
  const [selectedFindingId, setSelectedFindingId] = useState<string>('');
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [actionItems, setActionItems] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [representativeIndex, setRepresentativeIndex] = useState(0);
  // dirty 기준점 — 선택 발견사항이 바뀌는 시점에만 갱신한다(아래 프리필 effect). 매 렌더 prop에서
  // 파생하면 열린 채 상위가 findings를 refetch할 때 기준점이 이동해, 작성 중인 내용이 "변경 없음"으로
  // 뒤집힌다. ref로 고정해 parent refetch에 흔들리지 않게 한다.
  const baselineSnapshotRef = useRef<string>('');

  const selectedFinding = selectableFindings.find(f => f.id === selectedFindingId);
  const isUpdateMode = selectedFinding?.resolutionStatus === 'COMPLIED';

  // 모달 열릴 때 폼 초기화
  useEffect(() => {
    if (isOpen) {
      // 사전 선택된 발견사항이 있으면 고정
      if (preSelectedFindingId) {
        setSelectedFindingId(preSelectedFindingId);
      } else {
        // 첫 번째 OPEN 발견사항 자동 선택
        const firstOpen = selectableFindings.find(f => f.resolutionStatus === 'PENDING');
        setSelectedFindingId(firstOpen?.id || selectableFindings[0]?.id || '');
      }
      setPhotos([]);
      setActionItems([]);
    }
  }, [isOpen]);

  // 선택된 발견사항 변경(또는 재오픈) 시 프리필 (수정 모드는 기존 데이터, 새 해소는 권고 대책)
  useEffect(() => {
    if (!isOpen || !selectedFinding) return;
    const prefill = buildResolutionPrefill(selectedFinding);
    setActionItems(prefill.actionItems);
    setPhotos(prefill.photos);
    setRepresentativeIndex(prefill.representativeIndex);
    // 이 프리필을 dirty 기준점으로 고정 — 선택 변경/재오픈 시에만 갱신되고, 그 사이 상위
    // refetch로 finding prop이 바뀌어도 흔들리지 않는다.
    baselineSnapshotRef.current = resolutionSnapshot(prefill);
  }, [isOpen, selectedFindingId]);

  /**
   * dirty = 현재 값이 프리필 기준점과 다른가. 수정 모드는 무변경 저장 차단에, 생성 모드는 닫기
   * 보호(사진·조치 채우고 무경고로 나가는 것 방지)에 쓴다. 저장 게이트는 생성 모드에서 isDirty가
   * 아니라 조치사항 유효성으로만 판단하므로(푸터 submitDisabled), 생성 모드 dirty는 닫기 보호 전용이다.
   */
  const isDirty = selectedFinding
    ? resolutionSnapshot({ actionItems, photos, representativeIndex }) !==
      baselineSnapshotRef.current
    : false;

  const handleClose = () => {
    if (isLoading) return;
    // 미리보기 URL 정리
    photos.forEach(p => {
      if (p.file) URL.revokeObjectURL(p.previewUrl);
    });
    onClose();
  };

  const validateFile = useCallback((file: File): boolean => {
    if (!file.type.startsWith('image/')) {
      showToast('이미지 파일만 업로드할 수 있습니다.', 'error');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('파일 크기가 10MB를 초과합니다.', 'error');
      return false;
    }
    return true;
  }, [showToast]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      showToast(`최대 ${MAX_PHOTOS}장까지 업로드할 수 있습니다.`, 'error');
      return;
    }

    const validFiles = fileArray.filter(validateFile).slice(0, remaining);
    const newPhotos: PhotoPreview[] = validFiles.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  }, [photos.length, validateFile, showToast]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => {
      const removed = prev[index];
      if (removed.file) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
    setRepresentativeIndex(prev => {
      if (index === prev) return 0;
      if (index < prev) return prev - 1;
      return prev;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const effectiveNotes = itemsToNotes(actionItems);
    if (!selectedFindingId || actionItems.length === 0) {
      showToast('조치사항을 입력해주세요.', 'error');
      return;
    }
    // 버튼 disabled를 우회한 경로(입력 Enter 등)까지 무변경 저장을 막는다.
    if (isUpdateMode && !isDirty) return;

    setIsLoading(true);

    try {
      // 새로 추가된 사진 업로드
      const uploadedPhotos: Array<{ url: string; thumbnailUrl?: string; fileId?: string }> = [];

      for (const photo of photos) {
        if (photo.uploaded) {
          // 이미 업로드된 사진 (수정 모드)
          uploadedPhotos.push(photo.uploaded);
        } else if (photo.file) {
          // 새 사진 업로드
          const response = await uploadResolutionPhoto(photo.file, companyId);
          if (!response.success || !response.data) {
            throw new Error(response.error?.message || '사진 업로드에 실패했습니다.');
          }
          uploadedPhotos.push({
            url: response.data.url,
            fileId: response.data.id,
          });
        }
      }

      // 대표 사진을 배열 맨 앞으로 정렬
      const repIdx = representativeIndex < uploadedPhotos.length ? representativeIndex : 0;
      if (repIdx > 0 && uploadedPhotos.length > 1) {
        const [rep] = uploadedPhotos.splice(repIdx, 1);
        uploadedPhotos.unshift(rep);
      }
      const representativePhoto = uploadedPhotos[0];

      // 버퍼 모드: API 호출 없이 콜백으로 데이터 반환
      if (onBufferedSave) {
        onBufferedSave(selectedFindingId, {
          notes: effectiveNotes,
          photos: uploadedPhotos,
        });
        showToast('해소 정보가 수정되었습니다. (저장 시 반영됩니다)', 'info');
        handleClose();
        return;
      }

      // 해소 처리 API 호출 (직접 저장 모드)
      const response = await resolveWithEvidence(
        inspectionId,
        selectedFindingId,
        {
          resolutionNotes: effectiveNotes,
          resolutionPhotos: uploadedPhotos.length > 0 ? uploadedPhotos : undefined,
          resolutionPhotoUrl: representativePhoto?.url,
          resolutionPhotoId: representativePhoto?.fileId,
        },
        companyId,
      );

      if (!response.success) {
        throw new Error(response.error?.message || '해소 처리에 실패했습니다.');
      }

      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });

      showToast(
        isUpdateMode ? '해소 정보가 수정되었습니다.' : '위험요인이 해소되었습니다.',
        'success',
      );
      handleClose();
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openFindings = selectableFindings.filter(f => f.resolutionStatus === 'PENDING');
  const showDropdown = !preSelectedFindingId && selectableFindings.length > 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="위험요인 해소"
      size="lg"
      preventClose={isLoading}
      isDirty={isDirty}
      footer={
        <ResolutionFooter
          isUpdateMode={isUpdateMode}
          isDirty={isDirty}
          isLoading={isLoading}
          hasActionItems={actionItems.length > 0}
          onFallbackClose={handleClose}
        />
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="space-y-5">
          {/* 대상 위험요인 선택 */}
          {showDropdown ? (
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1.5">
                대상 위험요인
              </label>
              <select
                value={selectedFindingId}
                onChange={(e) => setSelectedFindingId(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 text-sm disabled:bg-ink-100"
              >
                {selectableFindings.map((f, idx) => {
                  const SEVERITY_LABEL: Record<string, string> = { LOW: '낮음', MEDIUM: '보통', HIGH: '높음', CRITICAL: '심각', GOOD: '우수' };
                  const severityLabel = SEVERITY_LABEL[f.severity] ?? f.severity;
                  const status = f.resolutionStatus === 'COMPLIED' ? ' (해소됨)' : '';
                  return (
                    <option key={f.id} value={f.id}>
                      {idx + 1}. {f.category} [{severityLabel}]{status}
                    </option>
                  );
                })}
              </select>
              {openFindings.length === 0 && (
                <p className="mt-1 text-xs text-green-600">모든 위험요인이 해소되었습니다.</p>
              )}
            </div>
          ) : selectedFinding ? (
            <div className="flex items-center gap-2">
              <SeverityBadge likelihoodScore={selectedFinding.aiLikelihoodScore} severityScore={selectedFinding.aiSeverityScore} config={riskConfig} size="sm" />
              <span className="text-sm font-medium text-ink-800">{selectedFinding.category}</span>
            </div>
          ) : null}

          {/* 해소 증빙 사진 */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">
              해소 증빙 사진 <span className="text-ink-400 font-normal">(선택)</span>
            </label>

            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {photos.map((photo, idx) => (
                  <div
                    key={idx}
                    className={`relative w-24 h-24 rounded-card-sm overflow-hidden border-2 cursor-pointer ${
                      idx === representativeIndex ? 'border-brand-500 ring-2 ring-brand-200' : 'border-line hover:border-ink-400'
                    }`}
                    onClick={() => setRepresentativeIndex(idx)}
                  >
                    <img
                      src={photo.previewUrl}
                      alt={`해소 사진 ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {!isLoading && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemovePhoto(idx); }}
                        className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full shadow hover:bg-red-600"
                        aria-label="사진 삭제"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    {idx === representativeIndex && photos.length > 1 && (
                      <div className="absolute bottom-0 left-0 right-0 bg-brand-500 text-white text-[10px] text-center py-0.5">
                        대표
                      </div>
                    )}
                  </div>
                ))}

                {/* 추가 버튼 */}
                {photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="w-24 h-24 rounded-card-sm border-2 border-dashed border-ink-300 flex flex-col items-center justify-center text-ink-400 hover:border-brand-500 hover:text-brand-500 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-xs mt-1">추가</span>
                  </button>
                )}
              </div>
            )}

            {photos.length === 0 && (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className={`border-2 border-dashed rounded-card p-5 text-center transition-colors cursor-pointer ${
                  isLoading
                    ? 'border-line bg-surface-soft cursor-not-allowed'
                    : 'border-ink-300 hover:border-brand-500'
                }`}
                onClick={() => !isLoading && fileInputRef.current?.click()}
              >
                <svg className="mx-auto h-8 w-8 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="mt-1.5 text-sm text-ink-600">클릭하거나 파일을 드래그하여 업로드</p>
                <p className="mt-0.5 text-xs text-ink-500">PNG, JPG, HEIC 지원 (최대 10MB, {MAX_PHOTOS}장)</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={isLoading}
            />
          </div>

          {/* 조치사항 */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              조치사항 <span className="text-red-500">*</span>
            </label>
            <ActionItemsInput
              items={actionItems}
              onChange={setActionItems}
              disabled={isLoading}
              placeholder="조치 항목 입력..."
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default HazardResolutionModal;
